import type {InMemoryCacheConfig} from '@apollo/client/cache/inmemory/types';
import {ProductInterface} from '__generated__/__types__';

export const cacheConfig: InMemoryCacheConfig = {
    typePolicies: {
        Query: {
            fields: {
                cmsPageWidget: {
                    keyArgs: ['id'], // specify the arguments that the field depends on
                    read(existingData, {args, toReference}) {
                        const id = args?.id ?? 'no-id';
                        return (
                            existingData?.[id] ??
                            toReference({
                                __typename: 'CmsPageResponse',
                                id: id,
                                url_key: args?.url_key ?? 'no-url-key',
                            })
                        );
                    },
                    merge(existing, incoming, {args}) {
                        const id = args?.id ?? 'no-id';
                        return {...existing, [id]: incoming};
                    },
                },
                productTag: {
                    keyArgs: ['identifier', 'products'], // specify the arguments that the field depends on
                    read(existingData, {args, toReference}) {
                        const identifier = args?.identifier ?? 'no-identifier';
                        const products = args?.products ?? [];
                        const skus = products.map((product: ProductInterface) => product.sku);
                        const key = `${identifier}-${skus.join('-')}`;
                        return (
                            existingData?.[key] ??
                            toReference({
                                __typename: 'Tag',
                                identifier: identifier,
                                products: products,
                            })
                        );
                    },
                    merge(existing, incoming, {args}) {
                        const identifier = args?.identifier ?? 'no-identifier';
                        const products = args?.products ?? [];
                        const skus = products.map((product: ProductInterface) => product.sku);
                        const key = `${identifier}-${skus.join('-')}`;
                        return {...existing, [key]: incoming};
                    },
                },
                customer: {
                    keyArgs: ['phone_number'],
                    read(existingData, {args, toReference}) {
                        const phoneNumber = args?.id ?? 'no-phone-number';
                        return (
                            existingData?.[phoneNumber] ??
                            toReference({
                                __typename: 'Customer',
                                phone_number: phoneNumber,
                            })
                        );
                    },
                    merge(existing, incoming) {
                        return {...existing, ...incoming};
                    },
                },

            },
        },
        CmsPageResponse: {
            keyFields: ['url_key'], // use url_key as the cache key
        },
        SimpleProduct: {
            keyFields: ['sku'], // use sku as the cache key
        },
        ProductImage: {
            keyFields: ['url'], // use url as the cache key
        },
        Tag: {
            keyFields: ['identifier'],
        },
        Products: {
            fields: {
                page_info: {
                    merge: true,
                },
                total_count: {
                    merge: true,
                },
                aggregations: {
                    merge(existing = [], incoming) {
                        return [...existing, ...incoming];
                    },
                },
                sort_fields: {
                    merge: true,
                },
                items: {
                    merge(existing = [], incoming) {
                        return [...existing, ...incoming];
                    },
                },
            },
        },
        FlashSaleItems: {
            fields: {
                page_info: {
                    merge: true,
                },
                total_count: {
                    merge: true,
                },
                items: {
                    merge(existing, incoming) {
                        return [...(existing ?? []), ...incoming];
                    },
                },
            },
        },
        CustomerReviewsResponse: {
            fields: {
                page_info: {
                    merge: true,
                },
                total_count: {
                    merge: true,
                },
                items: {
                    merge(existing, incoming) {
                        return [...(existing ?? []), ...incoming];
                    },
                },
            },
        },
        SelectedConfigurableOption: {
            keyFields: ['value_id']
        }
    },
};
